import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { driversService } from '../services/driversService';
import { toast } from 'react-toastify';
import { UserRole } from '../types';
import { RootState } from '../store/store';

interface DriverFormData {
  fullName: string;
  phone: string;
  email?: string;
  isActive?: boolean;
}

const DriverFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const canManageDrivers = user?.role === UserRole.MANAGER ||
                           user?.role === UserRole.ORG_ADMIN ||
                           user?.role === UserRole.PLATFORM_ADMIN ||
                           user?.role === UserRole.PROVIDER_MANAGER ||
                           user?.role === UserRole.PROVIDER_USER;

  const [loading, setLoading] = useState(isEditMode);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DriverFormData>({
    defaultValues: {
      isActive: true,
    },
  });

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const driver = await driversService.getById(id);
        reset({
          fullName: driver.fullName,
          phone: driver.phone,
          email: driver.email || '',
          isActive: driver.isActive,
        });
      } catch (error: any) {
        console.error('Failed to load driver:', error);
        toast.error('Failed to load driver data');
        navigate('/app/drivers');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate, reset]);

  const onSubmit = async (data: DriverFormData) => {
    try {
      console.log('Submitting driver data:', data);

      if (isEditMode && id) {
        await driversService.update(id, data);
        toast.success('Driver updated successfully');
      } else {
        await driversService.create(data);
        toast.success('Driver created successfully');
      }

      navigate('/app/drivers');
    } catch (error: any) {
      console.error('Failed to save driver:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to save driver';
      toast.error(errorMessage);
    }
  };

  if (!canManageDrivers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage drivers.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Driver' : 'New Driver'}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          {isEditMode
            ? 'Update driver information'
            : 'Add a new formal driver to your organization'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="bg-white shadow sm:rounded-lg p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                {...register('fullName', { required: 'Full name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Ahmet Yılmaz"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone *
              </label>
              <input
                type="tel"
                {...register('phone', { required: 'Phone is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+90 555 123 4567"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                {...register('email')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="driver@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Active Status (only show in edit mode) */}
            {isEditMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/app/drivers')}
            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[44px] font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:bg-blue-400 min-h-[44px] font-medium"
          >
            {isSubmitting
              ? 'Saving...'
              : isEditMode
              ? 'Update'
              : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DriverFormPage;
